import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {List} from 'immutable';

import type {ClassroomModelV1} from '@albert-io/models';
import {BREAKPOINTS} from 'client/constants';

// Below the `m` breakpoint the expanded sidemenu (296px) would consume most of the content area, so
// it starts collapsed there — still expandable. Guarded: jsdom has no matchMedia.
const startsCollapsed = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  !window.matchMedia(`(min-width: ${BREAKPOINTS.m}px)`).matches;

type State = {
  archivedClassrooms: List<ClassroomModelV1>;
  areArchivedClassroomsLoading: boolean;
  areClassroomsLoading: boolean;
  // True once the active-classroom fetch has settled at least once. Lets Home show a first-load state
  // without re-blanking on every subsequent refetch (which flips areClassroomsLoading per fetch).
  hasLoadedClassrooms: boolean;
  classrooms: List<ClassroomModelV1>;
  isShowingArchivedClassrooms: boolean;
  isSidemenuCollapsed: boolean;
  // The class whose context the sidemenu should keep showing on scope-neutral routes (e.g. Home).
  selectedClassId: string | null;
};

type Actions = {
  setArchivedClassrooms: (archivedClassrooms: State['archivedClassrooms']) => void;
  setAreArchivedClassroomsLoading: (
    areArchivedClassroomsLoading: State['areArchivedClassroomsLoading']
  ) => void;
  setAreClassroomsLoading: (areClassroomsLoading: State['areClassroomsLoading']) => void;
  setHasLoadedClassrooms: (hasLoadedClassrooms: State['hasLoadedClassrooms']) => void;
  setClassrooms: (classrooms: State['classrooms']) => void;
  setIsShowingArchivedClassrooms: (
    isShowingArchivedClassrooms: State['isShowingArchivedClassrooms']
  ) => void;
  setIsSidemenuCollapsed: (isSidemenuCollapsed: State['isSidemenuCollapsed']) => void;
  setSelectedClassId: (selectedClassId: State['selectedClassId']) => void;
};

const initialState: State = {
  archivedClassrooms: List([]),
  areArchivedClassroomsLoading: false,
  areClassroomsLoading: false,
  hasLoadedClassrooms: false,
  classrooms: List([]),
  isShowingArchivedClassrooms: false,
  isSidemenuCollapsed: startsCollapsed(),
  selectedClassId: null
};

export const useTeacherDashboardStore = create<State & Actions>()(
  devtools(
    immer((set) => ({
      ...initialState,
      setArchivedClassrooms: (archivedClassrooms) => {
        set((state) => {
          state.archivedClassrooms = archivedClassrooms;
        });
      },
      setAreArchivedClassroomsLoading: (areArchivedClassroomsLoading) => {
        set((state) => {
          state.areArchivedClassroomsLoading = areArchivedClassroomsLoading;
        });
      },
      setAreClassroomsLoading: (areClassroomsLoading) => {
        set((state) => {
          state.areClassroomsLoading = areClassroomsLoading;
        });
      },
      setHasLoadedClassrooms: (hasLoadedClassrooms) => {
        set((state) => {
          state.hasLoadedClassrooms = hasLoadedClassrooms;
        });
      },
      setIsShowingArchivedClassrooms: (isShowingArchivedClassrooms) => {
        set((state) => {
          state.isShowingArchivedClassrooms = isShowingArchivedClassrooms;
        });
      },
      setClassrooms: (classrooms) => {
        set((state) => {
          state.classrooms = classrooms;
        });
      },
      setIsSidemenuCollapsed: (isSidemenuCollapsed) => {
        set((state) => {
          state.isSidemenuCollapsed = isSidemenuCollapsed;
        });
      },
      setSelectedClassId: (selectedClassId) => {
        set((state) => {
          state.selectedClassId = selectedClassId;
        });
      }
    }))
  )
);
